import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { rests } from '@core/util/rests';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { UmagSupporterList } from '../../models/user';
import { HttpFormParams } from '@core/services/http-form-params';
import { OfficeUserCreateData, OfficeUserGroup, SingleOfficeUserData, UserSystemCodes } from '../../models/support/office-user-group';
import { map, tap } from 'rxjs/operators';
import { CommonFilterWithPaginator } from '../../models/filters/common-filter-with-paginator';
import { prepareFilter } from '@core/util/prepareFilter';
import { AppContextService } from '@core/services/app-context.service';
import { AuthService } from '@core/services/auth.service';
import { Permissions } from '@core/util/permissions.enum';
import { UserListFilter } from '@models/filters/list-by-uptime-filter';
import { Managers } from '@shared/interfaces/managers';
import { Supports } from '@shared/interfaces/supports';

@Injectable({
  providedIn: 'root',
})
export class UmagUserService {
  users: BehaviorSubject<UmagSupporterList> = new BehaviorSubject<UmagSupporterList>({ count: 0, users: [], userGroups: [] });
  userGroups: BehaviorSubject<OfficeUserGroup[]> = new BehaviorSubject<OfficeUserGroup[]>(null);
  private hasBeenCall = false;

  constructor(private http: HttpClient, private appContext: AppContextService) {}

  get allSupporters(): Observable<UmagSupporterList> {
    if (!this.hasBeenCall) {
      this.hasBeenCall = true;
      return this.userList(new CommonFilterWithPaginator(), true).pipe(tap((data) => this.users.next(data)));
    }
    return this.users;
  }

  userList(commonFilter: UserListFilter | CommonFilterWithPaginator, all = false, leaveSelf = false): Observable<UmagSupporterList> {
    const params = prepareFilter(commonFilter);
    const currentProfile = this.appContext.currentUserValue;

    if (all) {
      params.pageSize = '100000000';
      params.deleted = 'false';
    }

    return this.http.get<UmagSupporterList>(rests.user.umag.list, { params }).pipe(
      map((response) => {
        if (leaveSelf) {
          return response;
        }
        const filteredUsers = response.users.filter((b) => b.id !== currentProfile.user.id);
        return { ...response, users: filteredUsers };
      })
    );
  }

  getUser(userId): Observable<SingleOfficeUserData> {
    return this.http.get<SingleOfficeUserData>(rests.user.umag.get_user(userId));
  }

  userGroupList(): Observable<OfficeUserGroup[]> {
    if (AuthService.permissions.has(Permissions.OFFICE_ROLES_OBSERVE)) {
      return this.http.get<OfficeUserGroup[]>(rests.user.umag.user_group_list);
    }
    return of([]);
  }

  createUser(data: OfficeUserCreateData) {
    const body = new HttpFormParams().set('user', JSON.stringify(data));
    return this.http.post(rests.user.umag.create, body).pipe(tap(() => (this.hasBeenCall = false)));
  }

  updateUser(userId, data) {
    const body = new HttpFormParams().set('user', JSON.stringify(data));
    return this.http.post(rests.user.umag.edit_user(userId), body);
  }

  deleteUser(userId, takeStoresUserId) {
    const body = takeStoresUserId ? new HttpFormParams().set('takeStoresUserId', takeStoresUserId) : null;
    return this.http.post(rests.user.umag.delete_user(userId), body);
  }

  restoreUser(userId) {
    return this.http.post(rests.user.umag.restore_user(userId), null);
  }

  getUserGroupTechnicalSpecialists(userSystemCode: UserSystemCodes): Observable<{ id }> {
    return this.http.get<{ id }>(rests.user.umag.user_groups_lists, { params: { systemCode: userSystemCode } });
  }

  getManagers(): Observable<Managers[]> {
    return this.http.get<Managers[]>(rests.user.managers);
  }

  getSupports(): Observable<Supports[]> {
    return this.http.get<Supports[]>(rests.user.supports);
  }
}
